import React, { useState } from 'react';
import { VehiculeConfig, PersonnelConfig } from '../../../../../../shared/types/config';

/** Bloc « Priorité des grades sur l'engin » extrait de VehiculeModal.tsx. */

function syncGradePriority(
  form: Omit<VehiculeConfig, 'id'>,
  idGradesPriorite: number[]
): Omit<VehiculeConfig, 'id'> {
  return {
    ...form,
    id_grades_priorite: idGradesPriorite,
    id_grades_armement: idGradesPriorite,
  };
}

interface VehiculeGradePriorityProps {
  vehiculeForm: Omit<VehiculeConfig, 'id'>;
  setVehiculeForm: React.Dispatch<React.SetStateAction<Omit<VehiculeConfig, 'id'>>>;
  personnelConfigs: PersonnelConfig[];
}

const VehiculeGradePriority: React.FC<VehiculeGradePriorityProps> = ({
  vehiculeForm,
  setVehiculeForm,
  personnelConfigs,
}) => {
  const [searchGradesPrio, setSearchGradesPrio] = useState('');
  const [gradeToAppend, setGradeToAppend] = useState<number | ''>('');

  const gradesForService = personnelConfigs.filter(
    (p) => p.id_service === vehiculeForm.id_service && p.id != null && vehiculeForm.id_service > 0
  );
  const idGradesPrio = vehiculeForm.id_grades_priorite ?? vehiculeForm.id_grades_armement ?? [];

  const moveGrade = (index: number, dir: -1 | 1) => {
    const next = [...idGradesPrio];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    const t = next[index];
    next[index] = next[j];
    next[j] = t;
    setVehiculeForm(syncGradePriority(vehiculeForm, next));
  };

  const removeGradeAt = (index: number) => {
    const next = idGradesPrio.filter((_, i) => i !== index);
    setVehiculeForm(syncGradePriority(vehiculeForm, next));
  };

  const appendGrade = (personnelId: number) => {
    if (!personnelId || idGradesPrio.includes(personnelId)) return;
    setVehiculeForm(syncGradePriority(vehiculeForm, [...idGradesPrio, personnelId]));
    setGradeToAppend('');
  };

  return (
    <div className="col-span-2">
      <label className="block text-sm font-medium text-gray-300 mb-2">
        Priorité des grades sur l&apos;engin
        {idGradesPrio.length > 0 && (
          <span className="ml-2 text-xs text-blue-400">({idGradesPrio.length} en ordre)</span>
        )}
      </label>
      <p className="text-xs text-gray-500 mb-2">
        Ordre d&apos;affichage / hiérarchie des postes (chef d&apos;agrès, conducteur, etc.). Cela ne remplace pas les compétences d&apos;armement ci-dessus.
      </p>
      {!vehiculeForm.id_service ? (
        <p className="text-gray-400 text-sm">Choisissez un service pour lister les grades.</p>
      ) : gradesForService.length === 0 ? (
        <p className="text-gray-400 text-sm">Aucun grade pour ce service (onglet Personnels).</p>
      ) : (
        <>
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 mb-3 space-y-2 min-h-[3rem]">
            {idGradesPrio.length === 0 ? (
              <p className="text-gray-500 text-sm">Aucun ordre défini (optionnel).</p>
            ) : (
              idGradesPrio.map((pid, index) => {
                const label = personnelConfigs.find((p) => p.id === pid)?.grade ?? `ID ${pid}`;
                return (
                  <div
                    key={`${pid}-${index}`}
                    className="flex items-center gap-2 flex-wrap bg-gray-900/60 rounded px-2 py-1.5 border border-gray-700/80"
                  >
                    <span className="text-xs text-gray-500 w-6 tabular-nums">{index + 1}.</span>
                    <span className="text-sm text-gray-200 flex-1 min-w-[8rem]">{label}</span>
                    <button
                      type="button"
                      title="Monter"
                      disabled={index === 0}
                      onClick={() => moveGrade(index, -1)}
                      className="px-2 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      title="Descendre"
                      disabled={index === idGradesPrio.length - 1}
                      onClick={() => moveGrade(index, 1)}
                      className="px-2 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      title="Retirer"
                      onClick={() => removeGradeAt(index)}
                      className="px-2 py-1 text-xs rounded bg-red-900/50 hover:bg-red-800/60 text-red-200"
                    >
                      ×
                    </button>
                  </div>
                );
              })
            )}
          </div>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[12rem]">
              <label className="block text-xs text-gray-400 mb-1">Ajouter un grade à la fin</label>
              <select
                value={gradeToAppend === '' ? '' : String(gradeToAppend)}
                onChange={(e) => {
                  const v = e.target.value ? parseInt(e.target.value, 10) : '';
                  setGradeToAppend(v === '' ? '' : v);
                }}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Choisir —</option>
                {gradesForService
                  .filter((p) => p.id != null && !idGradesPrio.includes(p.id))
                  .filter((p) =>
                    searchGradesPrio
                      ? p.grade.toLowerCase().includes(searchGradesPrio.toLowerCase())
                      : true
                  )
                  .map((p) => (
                    <option key={p.id} value={p.id!}>
                      {p.grade}
                    </option>
                  ))}
              </select>
            </div>
            <button
              type="button"
              disabled={gradeToAppend === ''}
              onClick={() => typeof gradeToAppend === 'number' && appendGrade(gradeToAppend)}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg text-sm font-medium"
            >
              Ajouter
            </button>
          </div>
          {gradesForService.length > 6 && (
            <input
              type="text"
              placeholder="Filtrer la liste des grades à ajouter..."
              value={searchGradesPrio}
              onChange={(e) => setSearchGradesPrio(e.target.value)}
              className="mt-2 w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-white text-xs"
            />
          )}
        </>
      )}
    </div>
  );
};

export default VehiculeGradePriority;
